import React, { useState } from "react";
import { Heart, Star } from "lucide-react";
import StudentLayout from "../../layouts/StudentLayout";
import StudentHeaderSection from "../../components/student/StudentHeaderSection";
import {
  useGetMyCartQuery,
  useRemoveFromCartMutation,
} from "../../services/cart";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/common/ConfirmModal";
import LogoutModal from "../../components/student/LogoutModal";

const Wishlist = () => {
  const navigate = useNavigate();
  const { data: cartResponse } = useGetMyCartQuery();
  const cartItems = cartResponse?.data.items || [];
  const [removeFromCart] = useRemoveFromCartMutation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [showModal, setShowModal] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);

  const handleRemoveClick = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleConfirmRemove = async () => {
    if (selectedItem?._id) {
      try {
        await removeFromCart(selectedItem._id).unwrap();
      } catch (err) {
        // Optionally show error toast
      }
    }
    setShowModal(false);
    setSelectedItem(null);
  };

  const handleCancelRemove = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  return (
    <StudentLayout>
      <div className="sm:block hidden">
        <StudentHeaderSection
          isBtnIcon={false}
          btnText="Logout"
          onClick={() => setShowLogoutModal(true)}
        />
      </div>

      <div className="pt-12 md:pt-20 2xl:pt-28  pb-10 md:pb-16 2xl:pb-20 font-['Roboto']">
        <h1 className="font-['Inter'] px-4 sm:px-8 md:px-12 2xl:pl-[72px] text-2xl font-semibold tracking-[-1%] text-[#1D2026]">
          Wishlist ({cartItems.length})
        </h1>

        {cartItems.length > 0 && (
          <div className="mt-8 hidden border-y border-[#938A96] px-8 md:px-12 2xl:px-[77px] py-6 text-base font-medium text-[#1D2026] xl:grid xl:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(280px,0.9fr)] xl:gap-6">
            <div>Course</div>
            <div>Prices</div>
            <div className="text-center">Actions</div>
          </div>
        )}

        <div>
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center mx-4 md:mx-12 2xl:mx-24 px-5 py-24 bg-[#F8F9FB] rounded-2xl border border-[#E5E7EB] mt-4 md:mt-8 2xl:mt-12">
              <div className="mb-6">
                <span className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#F3F4F6]">
                  <Heart size={48} className="text-[#D1D5DB]" />
                </span>
              </div>
              <h2 className="text-2xl font-bold text-[#1D2026] mb-2">
                Your wishlist is empty
              </h2>
              <p className="text-lg text-[#6B7280] mb-6 text-center">
                Explore courses and add them to your wishlist!
              </p>
              <button
                className="bg-[#FF6636] hover:bg-[#e65a2e] text-white font-semibold px-8 py-3 rounded text-lg shadow"
                onClick={() => navigate("/student/courses")}
              >
                Browse Courses
              </button>
            </div>
          ) : (
            cartItems.map((item) => (
              <div
                key={item._id}
                className="border-b-[0.5px] border-[#938A96] px-4 sm:px-8 md:px-12 2xl:px-[73px] py-6 sm:py-7"
              >
                <div className="flex flex-col gap-5 xl:grid xl:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(280px,0.9fr)] xl:items-center xl:gap-6">
                  {/* Course */}
                  <div className="flex gap-4 sm:gap-6">
                    <img
                      src={item.courseThumbnail?.secureUrl}
                      alt={item.courseTitle}
                      className="h-[110px] w-[140px] sm:h-[150px] sm:w-[200px] shrink-0 object-cover"
                    />

                    <div className="flex min-w-0 flex-col justify-center">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm sm:text-[15px] text-[#6B7280]">
                        <span className="inline-flex items-center gap-1.5">
                          <Star
                            size={16}
                            className="fill-[#FFC107] text-[#FFC107]"
                          />
                          4.8 (256)
                        </span>
                        <span>Reviews</span>
                      </div>

                      <h2 className="mt-3 sm:mt-6 font-['Inter'] text-base sm:text-xl font-medium leading-snug tracking-[2%] text-[#000]">
                        {item.courseTitle}
                      </h2>
                    </div>
                  </div>

                  {/* Prices */}
                  <div className="grid grid-cols-2 gap-4 sm:gap-8 xl:gap-6">
                    <div className="xl:text-center">
                      <p className="text-lg sm:text-xl leading-6 text-[#FF6636]">
                        Rs {item.price}
                      </p>
                      <p className="mt-1.5 sm:mt-3 text-sm sm:text-[15px] text-[#6B7280]">
                        For Recorded Sessions
                      </p>
                    </div>

                    <div className="xl:text-center">
                      <p className="text-lg sm:text-xl leading-6 text-[#FF6636]">
                        Rs {item.price}
                      </p>
                      <p className="mt-1.5 sm:mt-3 text-sm sm:text-[15px] text-[#6B7280]">
                        For Live Sessions
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex w-full items-stretch gap-2 xl:justify-center">
                    <button
                      type="button"
                      className="h-12 sm:h-14 flex-1 xl:flex-none xl:min-w-[220px] 2xl:min-w-[256px] bg-[#E881051A] px-4 sm:px-8 font-['Inter'] text-base sm:text-lg font-semibold tracking-[-1%] text-[#FF6636] transition-colors hover:bg-[#fde7d2]"
                      onClick={() =>
                        navigate("/student/checkout", {
                          state: {
                            courseId: item.course._id,
                          },
                        })
                      }
                    >
                      Buy Now
                    </button>

                    <button
                      type="button"
                      className="flex h-12 w-12 sm:h-14 sm:w-16 xl:w-20 shrink-0 items-center justify-center bg-[#FF663633] text-[#FF6636] transition-colors hover:bg-[#f7d4c7]"
                      aria-label="Remove from wishlist"
                      onClick={() => handleRemoveClick(item)}
                    >
                      <Heart size={22} fill="currentColor" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <ConfirmModal
          open={showModal}
          title="Remove Course"
          message={
            <>
              Are you sure you want to remove{" "}
              <span className="font-bold">{selectedItem?.courseTitle}</span>{" "}
              from your wishlist?
            </>
          }
          confirmLabel="Remove"
          onConfirm={handleConfirmRemove}
          onCancel={handleCancelRemove}
        />

        {showLogoutModal && (
          <LogoutModal setShowLogoutModal={setShowLogoutModal} />
        )}
      </div>
    </StudentLayout>
  );
};

export default Wishlist;
